import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AreaDto, EquipmentDto, PanelDto } from '@app/shared';
import { api } from '../api';
import { useApp } from '../state';
import { Icon, LinkStatus, PageHead, plural } from '../ui';
import { TopBar } from './TopBar';
import { areaPath } from './Panels';
import { AreaFilter, loadFilter, saveFilter, useAreaMatch } from './AreaFilter';
import { AuthImg } from './AuthMedia';

/**
 * Вкладка «Оборудование»: все единицы своих участков. Тап — страница единицы:
 * панели, описание, инструкция, видео. Туда же ведёт QR-код на самом оборудовании.
 */
/** Порядок, заданный вручную (кнопка «Порядок»), при равном — по названию. */
const byOrder = (a: EquipmentDto, b: EquipmentDto) => a.sort - b.sort || a.name.localeCompare(b.name, 'ru');

/** Главное фото оборудования — или значок, если фото нет. */
export function Avatar({ e, size = 40 }: { e: Pick<EquipmentDto, 'avatarUrl' | 'name'>; size?: number }) {
  return (
    <span className={`mark${e.avatarUrl ? ' avatar' : ''}`} style={size !== 40 ? { width: size, height: size } : undefined}>
      {e.avatarUrl ? <AuthImg path={e.avatarUrl} /> : <Icon name="factory" size={Math.round(size * 0.55)} />}
    </span>
  );
}

export function EquipmentList() {
  const { open, fail, can } = useApp();
  const [items, setItems] = useState<EquipmentDto[] | null>(null);
  const [areas, setAreas] = useState<AreaDto[]>([]);
  const [panels, setPanels] = useState<PanelDto[]>([]);
  const [q, setQ] = useState('');
  // Режим «Порядок»: стрелки вверх/вниз у каждой единицы, порядок сохраняется сразу.
  const [ordering, setOrdering] = useState(false);
  const [area, setAreaState] = useState<string | null>(() => loadFilter('equipment'));
  const setArea = (v: string | null) => {
    setAreaState(v);
    saveFilter('equipment', v);
  };
  const match = useAreaMatch(areas, area);
  const canPanels = can('panel.view');

  const load = useCallback(async () => {
    try {
      const [e, a, p] = await Promise.all([api.equipment(), api.areas(), canPanels ? api.panels() : Promise.resolve([] as PanelDto[])]);
      setItems(e);
      setAreas(a);
      setPanels(p);
    } catch (err) {
      fail(err);
    }
  }, [fail, canPanels]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60_000);
    return () => clearInterval(t);
  }, [load]);

  const byPanel = useMemo(() => new Map(panels.map((p) => [p.id, p])), [panels]);

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = [...(items ?? [])]
      .sort(byOrder)
      .filter((e) => match(e.areaId) && (!needle || `${e.name} ${e.code} ${areaPath(areas, e.areaId)}`.toLowerCase().includes(needle)));
    const map = new Map<string, EquipmentDto[]>();
    for (const e of list) {
      const k = areaPath(areas, e.areaId);
      map.set(k, [...(map.get(k) ?? []), e]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b, 'ru'));
  }, [items, areas, q, match]);

  /** Поменять местами с соседом в той же группе и сохранить порядок всего списка. */
  async function move(e: EquipmentDto, dir: -1 | 1, group: EquipmentDto[]) {
    const other = group[group.indexOf(e) + dir];
    if (!other || !items) return;
    const all = [...items].sort(byOrder);
    const ia = all.findIndex((x) => x.id === e.id);
    const ib = all.findIndex((x) => x.id === other.id);
    [all[ia], all[ib]] = [all[ib]!, all[ia]!];
    const next = all.map((x, k) => ({ ...x, sort: (k + 1) * 10 }));
    setItems(next);
    try {
      await api.orderEquipment(next.map((x) => x.id));
    } catch (err) {
      fail(err);
      void load();
    }
  }

  return (
    <div className="stack">
      <TopBar />
      <PageHead title="Оборудование">
        {can('equipment.manage') && items && items.length > 1 && (
          <button type="button" className="btn btn-ghost btn-sm" aria-pressed={ordering} onClick={() => setOrdering((v) => !v)}>
            {ordering ? 'Готово' : 'Порядок'}
          </button>
        )}
        {can('equipment.manage') && !ordering && (
          <button type="button" className="btn btn-primary btn-sm" onClick={() => open({ name: 'unit' })}>
            <Icon name="plus" size={16} />
            Добавить
          </button>
        )}
      </PageHead>

      {items && items.length > 0 && <AreaFilter areas={areas} used={items.map((e) => e.areaId)} value={area} onChange={setArea} />}

      {items && items.length > 5 && (
        <div className="search">
          <Icon name="search" size={18} />
          <input className="input" type="search" placeholder="Поиск: название, номер ШТ" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Поиск оборудования" />
        </div>
      )}

      {!items && (
        <>
          <div className="skeleton" />
          <div className="skeleton" />
        </>
      )}

      {items && items.length === 0 && (
        <div className="card empty">
          <span className="mark">
            <Icon name="factory" />
          </span>
          <h2 className="h2">Оборудования пока нет</h2>
          <p className="muted" style={{ margin: 0 }}>
            {can('equipment.manage')
              ? 'Добавьте станок: заводской номер, участок, панели, описание и видео. Или отсканируйте QR-код на оборудовании — приложение предложит его завести.'
              : 'Здесь появятся станки ваших участков: панели, инструкции и видео. Можно просто отсканировать QR-код на оборудовании.'}
          </p>
          {can('equipment.manage') && (
            <button type="button" className="btn btn-primary" onClick={() => open({ name: 'unit' })}>
              Добавить оборудование
            </button>
          )}
        </div>
      )}

      {groups.map(([path, list]) => (
        <section key={path} className="stack" style={{ gap: 8 }}>
          <h2 className="section-title">
            {path} · {list.length}
          </h2>
          <div className="list">
            {list.map((e) => {
              const ps = e.panelIds.map((id) => byPanel.get(id)).filter((p): p is PanelDto => !!p);
              const pos = list.indexOf(e);
              if (ordering) {
                return (
                  <div key={e.id} className="item">
                    <Avatar e={e} />
                    <span className="grow stack" style={{ gap: 3 }}>
                      <span className="title ellipsis">{e.name}</span>
                      <span className="sub ellipsis">{e.code}</span>
                    </span>
                    <button type="button" className="icon-btn" aria-label={`Выше: ${e.name}`} disabled={pos === 0} onClick={() => void move(e, -1, list)}>
                      <Icon name="up" size={18} />
                    </button>
                    <button type="button" className="icon-btn" aria-label={`Ниже: ${e.name}`} disabled={pos === list.length - 1} onClick={() => void move(e, 1, list)}>
                      <Icon name="down" size={18} />
                    </button>
                  </div>
                );
              }
              return (
                <button key={e.id} type="button" className="item" onClick={() => open({ name: 'unit', id: e.id })}>
                  <Avatar e={e} />
                  <span className="grow stack" style={{ gap: 3 }}>
                    <span className="title ellipsis">{e.name}</span>
                    <span className="sub ellipsis">{e.code}</span>
                    {(ps.length > 0 || e.fileCount > 0) && (
                      <span className="row wrap" style={{ gap: 8 }}>
                        {ps.length === 1 && <LinkStatus link={ps[0]!.link} />}
                        {ps.length > 1 && (
                          <span className="small muted">
                            {ps.length} {plural(ps.length, 'панель', 'панели', 'панелей')} · на связи {ps.filter((p) => p.link.ok).length}
                          </span>
                        )}
                        {e.fileCount > 0 && (
                          <span className="badge outline">
                            <Icon name="doc" size={13} />
                            {e.fileCount}
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                  <span className="chev">
                    <Icon name="right" size={18} />
                  </span>
                </button>
              );
            })}
          </div>
        </section>
      ))}

      {items && items.length > 0 && groups.length === 0 && <p className="muted">{q ? `Ничего не нашлось по запросу «${q}».` : 'На этом участке оборудования нет.'}</p>}
    </div>
  );
}
